import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import searchIcon from 'public/static/images/paid_landing/search-icon.svg';
import poweredByGoogle from 'public/static/images/powered-by-google.png';
import { useDebouncedCallback } from 'use-debounce';
import { colors, screen, fonts } from './styles/variables';

const DOWN_KEYCODE = 40;
const UP_KEYCODE = 38;
const ENTER_KEYCODE = 13;

interface ResultType {
  displayName: string;
  valid: boolean;
}

const Attribution = (): JSX.Element => (
  <div className="contribution-container">
    <img className="powered-by-google-img" src={poweredByGoogle} alt="Powered by Google" />
    <style jsx>
      {`
        .contribution-container {
          text-align: center;
        }
        .powered-by-google-img {
          margin-top: 15px;
          width: 150px;
        }
      `}
    </style>
  </div>
);

interface ResultsProps<R extends ResultType> {
  results: R[];
  needsAttribution: boolean;
  selectedIndex?: number;
  onSelect: (result: R) => void;
  currentLocation?: JSX.Element;
  onSelectCityTap: () => Promise<void>;
}

export function Results<R extends ResultType>({
  results,
  needsAttribution,
  selectedIndex,
  onSelect,
  onSelectCityTap,
  currentLocation,
}: ResultsProps<R>): JSX.Element {
  return (
    <div data-location-results>
      <ul className="location-results">
        {currentLocation}

        {(results || []).map((result, index) => (
          <Result<R>
            key={result.displayName}
            selected={selectedIndex === index}
            result={result}
            onSelectCityTap={onSelectCityTap}
            onSelect={onSelect}
          />
        ))}
      </ul>
      {needsAttribution && <Attribution />}
      <style jsx>
        {`
          .location-results {
            margin: 0;
            padding: 0;
          }

          ul.location-results > :global(li) {
            list-style: none;
            text-align: left;
            cursor: pointer;
            font-size: 14px;
            font-weight: 400;
            color: ${colors.BLACK};
            position: relative;
            padding: 16px;
          }
        `}
      </style>
    </div>
  );
}

interface ResultProps<R extends ResultType> {
  selected: boolean;
  result: R;
  onSelect: (result: R) => void;
  onSelectCityTap: () => Promise<void>;
}

function Result<R extends ResultType>({
  selected,
  result,
  onSelect,
  onSelectCityTap,
}: ResultProps<R>): JSX.Element {
  const text = result.displayName;
  return (
    <li
      className={selected ? '--selected' : ''}
      onClick={async (): Promise<void> => {
        onSelect(result);
        onSelectCityTap();
      }}
      data-location={result.displayName}
    >
      {text}
      <style jsx>{`
        li:hover,
        li.--selected {
          background-color: ${colors.G1};
        }
      `}</style>
    </li>
  );
}

interface LocationAutocompleteProps<R extends ResultType> {
  needsAttribution: boolean;
  currentLocation?: JSX.Element;
  onSelect: (result: R) => void;
  fetchResults({ query }: { query: string }): Promise<R[]>;
  onInputTap?: () => Promise<void>;
  onSelectCityTap?: () => Promise<void>;
  className?: string;
}

export default function LocationAutocompleteComponent<R extends ResultType>({
  needsAttribution,
  onSelect,
  fetchResults,
  currentLocation,
  onInputTap = (): Promise<void> => Promise.resolve(),
  onSelectCityTap = (): Promise<void> => Promise.resolve(),
  className,
}: LocationAutocompleteProps<R>): JSX.Element {
  const [results, setResults] = useState<R[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>();
  const { t } = useTranslation();

  async function updateResults(input: string): Promise<void> {
    setSelectedIndex(undefined);

    if (input.length === 0) {
      setResults([]);
    } else {
      const newResults = await fetchResults({ query: input });

      const withCityAndState = newResults.filter((result) => result.valid);

      setResults(withCityAndState);
    }
  }

  const debouncedUpdateReults = useDebouncedCallback(updateResults, 500);

  function handleArrowUp(): void {
    if (!selectedIndex) {
      return;
    }

    if (selectedIndex <= 0) {
      setSelectedIndex(0);
      return;
    }

    setSelectedIndex(selectedIndex - 1);
  }

  function handleArrowDown(): void {
    if (typeof selectedIndex === 'undefined') {
      setSelectedIndex(0);
      return;
    }

    if (selectedIndex >= results.length - 1) {
      setSelectedIndex(results.length - 1);
      return;
    }

    setSelectedIndex(selectedIndex + 1);
  }

  async function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>): Promise<void> {
    if ([DOWN_KEYCODE, UP_KEYCODE, ENTER_KEYCODE].includes(event.keyCode)) {
      event.preventDefault();
    }

    // arrow up
    if (event.keyCode === UP_KEYCODE) {
      handleArrowUp();
      // arrow down
    } else if (event.keyCode === DOWN_KEYCODE) {
      handleArrowDown();
    } else if (event.keyCode === ENTER_KEYCODE && typeof selectedIndex !== 'undefined') {
      // enter key
      const result = results[selectedIndex];
      if (result) {
        await onSelect(result);
      }
    }
  }

  return (
    <div data-location-autocomplete className={className}>
      <div className="input-container">
        <div className="search-icon" />
        <input
          onClick={(): Promise<void> => onInputTap()}
          type="text"
          name="location"
          placeholder={t('contentDashboard.enterACity')}
          autoComplete="off"
          autoFocus={true}
          onKeyDown={onKeyDown}
          onChange={(e): void => {
            debouncedUpdateReults(e.target.value);
          }}
        />
      </div>
      <div className="body">
        <div className="scrolling-container">
          <Results
            needsAttribution={needsAttribution}
            results={results}
            currentLocation={currentLocation}
            onSelect={onSelect}
            onSelectCityTap={onSelectCityTap}
            selectedIndex={selectedIndex}
          />
        </div>
      </div>
      <style jsx>
        {`
          .header {
            padding: 17px 10px 10px 14px;
            border-bottom: 1px solid ${colors.G2};
            text-align: left;
            height: 60px;
            position: relative;
            font-size: 20px;
            font-weight: 600;
            color: ${colors.BLACK};
            background-color: white;
            border-radius: 5px;
            text-align: center;
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
          }

          .body {
            overflow: hidden;
          }

          .scrolling-container {
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
          }

          @media (min-width: ${screen.smMin}) {
            .scrolling-container {
              height: 100%;
              min-height: 472px;
            }
          }

          .location-autocomplete-container {
            width: 298px;
            height: 465px;
          }

          // autocomplete styles

          .input-container {
            position: relative;
            padding: 8px 16px;
            box-shadow: 0 1px 0 0 ${colors.G2};
          }

          .search-icon {
            position: absolute;
            z-index: 1;
            height: 22px;
            width: 22px;
            top: 25px;
            left: 21px;
            background-image: url('${searchIcon}');
            background-size: cover;
          }

          input {
            background-color: white;
            border: none;
            height: 48px;
            width: 100%;
            font-size: 16px;
            line-height: 24px;
            font-weight: 400;
            letter-spacing: 0.2px;
            color: ${colors.BLACK};
            padding: 12px 5px 5px 42px;
            -webkit-appearance: none;
            border-radius: 8px;
            outline: none;
            font-family: ${fonts.default};
          }

          input ::placeholder {
            color: ${colors.G3};
            font-weight: 100;
          }
        `}
      </style>
    </div>
  );
}
